/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FormEvent, type ReactElement, useState } from 'react';
import { Search as SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';
import FlyoutShell from './flyout-shell';
import FlyoutSocial from './flyout-social';

interface SearchFlyoutProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Search flyout (UCR-2762) — a bottom sheet opened by the header's search icon (mobile + desktop).
 * Holds a "Search" heading, a search field that submits to the search results page, and the social
 * row. Chrome (swipe/tap-to-close, drag handle, height) comes from {@link FlyoutShell}.
 *
 * The heading and field are `pinned` rather than part of the scrollable body so that they stay
 * visible when iOS shrinks the sheet to sit above the on-screen keyboard (UCR-2984). The field is
 * focused by the shell's `autoFocus`, after the open animation — not by an `autoFocus` attribute
 * here, which fired mid-animation and let Safari scroll the field out of view.
 */
export default function SearchFlyout({ open, onOpenChange }: SearchFlyoutProps): ReactElement {
    const { t } = useTranslation('header');
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const title = t('searchTitle', { defaultValue: 'Search' });

    const onSubmit = (event: FormEvent) => {
        event.preventDefault();
        const q = query.trim();
        if (!q) return;
        onOpenChange(false);
        void navigate(`/search?q=${encodeURIComponent(q)}`, { state: { query: q } });
    };

    return (
        <FlyoutShell
            open={open}
            onOpenChange={onOpenChange}
            title={title}
            // Shorter than the nav sheet's default: the field has to clear the keyboard, and there is
            // little else in here to fill the space.
            bodyClassName="min-h-[22dvh]"
            pinned={
                // `data-vaul-no-drag` opts this block out of vaul's swipe-to-dismiss, so a swipe
                // aimed at the field can't close the sheet out from under the user.
                <div data-vaul-no-drag className="flex min-h-0 flex-col">
                    {/* The heading absorbs the shortfall when the keyboard leaves the sheet very short
                        (landscape): it clips rather than pushing the field off the bottom. Its spacing
                        lives on the inner `<p>`, not on this wrapper or as a flex `gap` — padding and
                        gap can't shrink, so either would re-enter the incompressible floor. The form
                        below stays `shrink-0`, so the field itself is never given up. */}
                    <div className="min-h-0 overflow-hidden">
                        <p className="pb-3 pt-6 font-display text-[28px] font-black uppercase leading-[0.9] tracking-[-0.05em]">
                            {title}
                        </p>
                    </div>
                    <form onSubmit={onSubmit} className="flex shrink-0 items-center gap-2.5 py-3.5 pr-5">
                        <SearchIcon aria-hidden className="size-[26px] shrink-0" />
                        <input
                            type="search"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder={t('searchPlaceholder')}
                            aria-label={t('searchPlaceholder')}
                            className="h-9 min-w-0 flex-1 border border-bm-black bg-transparent px-2 text-base text-bm-black outline-none placeholder:text-bm-black/50"
                        />
                    </form>
                </div>
            }>
            <FlyoutSocial className="mt-auto pb-12" />
        </FlyoutShell>
    );
}
